import { DefaultProjectorFn, MemoizedSelector, createSelector } from "@ngrx/store";
import { ClaimedReward, Reward, WalletConfig } from "src/app/services/api";
import { merge } from "src/app/utils/merge/merge";
import { getAvailableCouponCodes } from "src/app/utils/coupon-code-status";

import { selectProgram } from "../app/selectors";
import { RootState } from "..";

import { UserState } from "./state";

const selectFeature = (state: RootState): UserState => state.user;

export const selectLoading = createSelector(selectFeature, state => state.loading);

export const selectError = createSelector(selectFeature, state => state.error);

export const selectErrorParams = createSelector(selectFeature, state => state.errorParams);

export const selectDownloadingCouponPass = createSelector(selectFeature, state => state.downloadingCouponPass);

export const selectCustomerData = createSelector(selectFeature, state => state.customerData);

export const selectRewards = createSelector(selectFeature, state => Object.values(state.rewards)
  .filter(reward => reward.nonRedeemableReason !== Reward.NonRedeemableReasonEnum.MaxRedemptionTimesReached),
);

export const selectAuthToken = createSelector(selectFeature, state => state.authToken);
export const selectDownloadToken = createSelector(selectFeature, state => state.downloadToken);
export const selectVerifyToken = createSelector(selectFeature, state => state.verifyToken);

export const selectUserId = createSelector(selectFeature, state => state.userId);

export const selectReferral = createSelector(selectFeature, state => state.referral);

export const selectUTMValues = createSelector(selectFeature, state => state.utm);

export const selectReward = (id: string): MemoizedSelector<RootState, Reward, DefaultProjectorFn<Reward>> =>
  createSelector(selectFeature, state => state.rewards[id]);

export const selectClaimedReward = (id: string): MemoizedSelector<RootState, ClaimedReward, DefaultProjectorFn<ClaimedReward>> =>
  createSelector(selectFeature, state => state.claimedRewards[id]);

export const selectWalletConfig = createSelector(
  selectFeature,
  selectProgram,
  (userState, program) => {
    const baseWalletConfig = program?.configurations?.wallet;
    const tierWalletConfig = program?.configurations?.tiers?.[userState.customerData?.customer?.loyaltyData?.tierStatus];
    const walletConfig = merge<WalletConfig>(baseWalletConfig, tierWalletConfig || {});

    if (!baseWalletConfig || !tierWalletConfig) return walletConfig;

    const tierStoreCard = tierWalletConfig.ios?.storeCard;
    const baseStoreCard = baseWalletConfig.ios?.storeCard;
    const clearIosComposite =
      !tierStoreCard?.stripWithOverlay &&
      ((tierStoreCard?.strip !== undefined && tierStoreCard.strip !== baseStoreCard?.strip) ||
        (tierStoreCard?.stripOverlay !== undefined && tierStoreCard.stripOverlay !== baseStoreCard?.stripOverlay));

    const clearAndroidComposite =
      !tierWalletConfig.android?.heroWithOverlay &&
      ((tierWalletConfig.android?.heroImage !== undefined &&
        tierWalletConfig.android.heroImage !== baseWalletConfig.android?.heroImage) ||
        (tierWalletConfig.android?.heroOverlay !== undefined &&
          tierWalletConfig.android.heroOverlay !== baseWalletConfig.android?.heroOverlay));

    if (!clearIosComposite && !clearAndroidComposite) return walletConfig;

    return {
      ...walletConfig,
      ...(clearIosComposite
        ? {
            ios: {
              ...walletConfig.ios,
              storeCard: {
                ...walletConfig.ios.storeCard,
                stripWithOverlay: undefined,
              },
            },
          }
        : {}),
      ...(clearAndroidComposite
        ? {
            android: {
              ...walletConfig.android,
              heroWithOverlay: undefined,
            },
          }
        : {}),
    };
  },
);

export const selectTransactions = createSelector(selectFeature, state => state.transactions);

export const selectCustomerCodes = createSelector(selectFeature, state => state.customerCodes);

export const selectProfileFormState = createSelector(selectFeature, state => state.profileForm);

export const selectProfileForm = createSelector(selectProfileFormState, state => state.data);

export const selectCustomerCodesLoading = createSelector(selectFeature, state => state.customerCodesLoading);

export const selectAvailableCustomerCodes = createSelector(selectCustomerCodes, codes => getAvailableCouponCodes(codes ?? []));
